"use client";

import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useAuth } from "@tillix/tx-core/react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@tillix/ui-atoms/atoms/alert";
import { Button } from "@tillix/ui-atoms/button";
import { Form } from "@tillix/ui-atoms/form";
import { FormRootError } from "@tillix/ui-atoms/form-root-error";
import { CheckCircle2 } from "lucide-react";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import { useT } from "@/features/i18n/hooks/use-t";
import EmailInput from "../fields/EmailInput";
import {
  defaultForgotPasswordFormValues,
  ForgotPasswordFormSchema,
  type ForgotPasswordFormValues,
} from "../schemas/forgot-password";

export default function ForgotPasswordForm() {
  const authStore = useAuth();
  const [emailSent, setEmailSent] = useState(false);
  const { t } = useT();

  const form = useForm<ForgotPasswordFormValues>({
    resolver: arktypeResolver(ForgotPasswordFormSchema),
    defaultValues: defaultForgotPasswordFormValues,
    mode: "onChange",
  });
  const { errors } = useFormState({ control: form.control });
  const rootError = errors.root?.message;

  const handleSubmit = async (values: ForgotPasswordFormValues) => {
    const result = await authStore.requestPasswordReset({
      email: values.email,
    });

    if (result.error) {
      console.error("failed to request password reset, error:", {
        code: result.error.code,
        message: result.error.message,
      });
      form.setError("root", {
        message: result.error.code ?? result.error.message,
      });
    } else {
      setEmailSent(true);
    }
  };

  if (emailSent) {
    return (
      <Alert variant="success">
        <CheckCircle2 />
        <AlertTitle>{t("auth.checkYourEmail")}</AlertTitle>
        <AlertDescription>{t("auth.resetInstructionsSent")}</AlertDescription>
      </Alert>
    );
  }

  return (
    <FormProvider {...form}>
      <Form
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <EmailInput<ForgotPasswordFormValues> />
        <Button
          className="w-full"
          disabled={form.formState.isSubmitting || !form.formState.isValid}
          type="submit"
        >
          {form.formState.isSubmitting
            ? t("auth.sendingResetLink")
            : t("auth.sendResetLink")}
        </Button>
        <FormRootError keyPrefix="auth" rootError={rootError} variant="card" />
      </Form>
    </FormProvider>
  );
}
